const SECURITY_POLICY_MODES = ['monitor', 'step_up', 'enforce'];

export const SECURITY_POLICY_RULES = [
  {
    key: 'profileViewBurst',
    ruleId: 'profile_view_burst',
    labelKey: 'securityPolicy.profileViewBurst',
    descriptionKey: 'securityPolicy.profileViewBurstDescription',
    fields: [
      { key: 'threshold', labelKey: 'securityPolicy.threshold' },
      { key: 'windowSeconds', labelKey: 'securityPolicy.windowSeconds' },
    ],
  },
  {
    key: 'piiRevealBurst',
    ruleId: 'pii_reveal_burst',
    labelKey: 'securityPolicy.piiRevealBurst',
    descriptionKey: 'securityPolicy.piiRevealBurstDescription',
    fields: [
      { key: 'threshold', labelKey: 'securityPolicy.threshold' },
      { key: 'windowSeconds', labelKey: 'securityPolicy.windowSeconds' },
    ],
  },
  {
    key: 'supportSessionBroadAccess',
    ruleId: 'support_session_broad_access',
    labelKey: 'securityPolicy.supportSessionBroadAccess',
    descriptionKey: 'securityPolicy.supportSessionBroadAccessDescription',
    fields: [
      { key: 'profileThreshold', labelKey: 'securityPolicy.profileThreshold' },
      { key: 'revealThreshold', labelKey: 'securityPolicy.revealThreshold' },
      { key: 'windowSeconds', labelKey: 'securityPolicy.windowSeconds' },
    ],
  },
  {
    key: 'bulkDownloadAfterBrowse',
    ruleId: 'bulk_download_after_browse',
    labelKey: 'securityPolicy.bulkDownloadAfterBrowse',
    descriptionKey: 'securityPolicy.bulkDownloadAfterBrowseDescription',
    fields: [
      { key: 'profileViewThreshold', labelKey: 'securityPolicy.profileViewThreshold' },
      { key: 'downloadThreshold', labelKey: 'securityPolicy.downloadThreshold' },
      { key: 'windowSeconds', labelKey: 'securityPolicy.windowSeconds' },
    ],
  },
];

const DEFAULT_RULE_VALUES = {
  profileViewBurst: { mode: 'monitor', threshold: 1, windowSeconds: 60 },
  piiRevealBurst: { mode: 'monitor', threshold: 1, windowSeconds: 60 },
  supportSessionBroadAccess: {
    mode: 'monitor',
    profileThreshold: 1,
    revealThreshold: 1,
    windowSeconds: 60,
  },
  bulkDownloadAfterBrowse: {
    mode: 'monitor',
    profileViewThreshold: 1,
    downloadThreshold: 1,
    windowSeconds: 60,
  },
};

const INTEGER_FIELD_BOUNDS = {
  threshold: { min: 1, max: 10000 },
  profileThreshold: { min: 1, max: 10000 },
  revealThreshold: { min: 1, max: 10000 },
  profileViewThreshold: { min: 1, max: 10000 },
  downloadThreshold: { min: 1, max: 10000 },
  windowSeconds: { min: 1, max: 86400 },
};

function pickRuleSource(policy, rule) {
  return policy?.[rule.key] || policy?.[rule.ruleId] || {};
}

function normalizeMode(value) {
  return SECURITY_POLICY_MODES.includes(value) ? value : 'monitor';
}

function normalizeInteger(value, fallback = 1, bounds = {}) {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  const number = Number(value);
  if (!Number.isFinite(number)) {
    return fallback;
  }

  const integer = Math.trunc(number);
  if (
    (bounds.min !== undefined && integer < bounds.min)
    || (bounds.max !== undefined && integer > bounds.max)
  ) {
    return fallback;
  }

  return integer;
}

function normalizeFieldInteger(value, fieldKey, fallback = 1) {
  return normalizeInteger(value, fallback, INTEGER_FIELD_BOUNDS[fieldKey]);
}

function normalizeRule(policy, rule) {
  const defaults = DEFAULT_RULE_VALUES[rule.key];
  const source = pickRuleSource(policy, rule);

  return rule.fields.reduce((result, field) => ({
    ...result,
    [field.key]: normalizeFieldInteger(source?.[field.key], field.key, defaults[field.key]),
  }), {
    mode: normalizeMode(source?.mode || defaults.mode),
  });
}

export function mapSecurityPolicyResponseToDto(response) {
  const policy = response?.policy || response || {};

  return SECURITY_POLICY_RULES.reduce((result, rule) => ({
    ...result,
    [rule.key]: normalizeRule(policy, rule),
  }), {});
}

export function mapSecurityPolicyToUpdatePayload(policy) {
  return SECURITY_POLICY_RULES.reduce((result, rule) => {
    const source = policy?.[rule.key] || {};

    return {
      ...result,
      [rule.key]: rule.fields.reduce((ruleResult, field) => ({
        ...ruleResult,
        [field.key]: normalizeFieldInteger(
          source[field.key],
          field.key,
          DEFAULT_RULE_VALUES[rule.key][field.key],
        ),
      }), {
        mode: normalizeMode(source.mode),
      }),
    };
  }, {});
}
